import {
  digitRule,
  lowercaseLetterRule,
  minLengthPasswordRule,
  specialCharRule,
  uppercaseLetterRule,
  useLazyQuery,
  useMutation,
  useMyUrl,
  useTranslation,
} from '@gimlite/common';
import {
  Button,
  Col,
  Empty,
  Form,
  Header,
  Icon,
  Input,
  Loading,
  Space,
  Widget,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import OspFull from '../../assets/logos/osp_full';
import {
  Mutation,
  MutationVerifyAndUpdateUserArgs,
  Query,
  QueryVerifyUserArgs,
} from '../../client/graphql';
import { verifyAndUpdateUserGQL } from '../../common/gql/verifyAndUpdateUser.gql';
import { verifyUserGQL } from '../../common/gql/verifyUser.gql';
import './tenant-confirm.scss';

const debug = false;

export const TenantConfirm = observer(() => {
  const { t } = useTranslation();
  const { getParamsUrl } = useMyUrl<{ token: string }>();
  const [error, setError] = useState<'LINK' | 'TOKEN' | null>();
  const [passwordValidFormat, setPasswordValidForm] = useState(false);
  const [passwordValidSame, setPasswordValidSame] = useState(false);
  const [passwordValues, setPasswordValues] = useState<Object>({});

  const [verifyUserCall, verifyUserState] = useLazyQuery<
    {
      verifyUser: Query['verifyUser'];
    },
    QueryVerifyUserArgs
  >(verifyUserGQL);

  const [verifyAndUpdateUserCall, verifyAndUpdateUserState] = useMutation<
    {
      verifyAndUpdateUser: Mutation['verifyAndUpdateUser'];
    },
    MutationVerifyAndUpdateUserArgs
  >(verifyAndUpdateUserGQL, {
    notification: {
      success: t('thePasswordHasBeenRegistered'),
      error: t('thePasswordCouldNotBeRegistered'),
    },
  });

  useEffect(() => {
    if (getParamsUrl.token) {
      verifyUserCall({ variables: { token: getParamsUrl.token } });
    } else {
      setError(() => 'LINK');
    }
  }, [getParamsUrl]);

  useEffect(() => {
    if (verifyUserState.error) {
      setError(() => 'TOKEN');
    }
  }, [verifyUserState.error]);

  useEffect(() => {
    if (verifyAndUpdateUserState.data) {
      if (!debug) {
        window.location.replace('/');
      }
    }
  }, [verifyAndUpdateUserState.data]);

  return (
    <div className="tenantConfirm">
      <Header config={{ logo: OspFull(), withAccount: false }}></Header>
      {verifyUserState.loading ? (
        <div className="tenantConfirm__load">
          <Loading></Loading>
          <Write
            className="tenantConfirm__load__text"
            data={{ item: t('waitingForServerResponse') }}
            config={{ mode: 'value-medium' }}
          ></Write>
        </div>
      ) : error ? (
        <div className="tenantConfirm__error">
          <Icon
            config={{
              type: 'faCircleExclamationSolid',
              color: 'error',
              size: 'giga',
            }}
          ></Icon>
          <Write
            className="tenantConfirm__error__text"
            data={{
              item:
                error === 'LINK'
                  ? t('linkIsNoLongerValid')
                  : t('linkIsNotValid'),
            }}
            config={{ mode: 'value-large', color: 'error' }}
          ></Write>
        </div>
      ) : (
        <Zone
          className="tenantConfirm__contain"
          config={{
            zones: [['auth']],
            rows: ['1fr'],
            columns: ['1fr'],
          }}
        >
          <Zone.Area
            config={{
              area: 'auth',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '700px',
                }}
              >
                <Widget config={{ title: t('newAccount') }}>
                  <Form
                    config={{
                      validateOnChange: true,
                    }}
                    data={{
                      value: passwordValues,
                    }}
                    handleEvent={{
                      submit: async (value) => {
                        await verifyAndUpdateUserCall({
                          variables: {
                            input: {
                              token: getParamsUrl.token!,
                              user: {
                                username:
                                  verifyUserState.data?.verifyUser.user
                                    .username!,
                                password: value.newPassword,
                              },
                            },
                          },
                        });
                      },
                      validate: (isValid) => {
                        setPasswordValidForm(() => isValid);
                      },
                      change: (values) => {
                        setPasswordValues(() => values);
                        setPasswordValidSame(
                          () =>
                            values.newPassword === values.confirmNewPassword,
                        );
                      },
                    }}
                  >
                    <Form.Item
                      config={{
                        name: 'newPassword',
                        label: t('newPassword'),
                        way: 'vertical',
                        rules: [
                          { required: true },
                          {
                            validator: minLengthPasswordRule,
                          },
                          {
                            validator: uppercaseLetterRule,
                          },
                          {
                            validator: lowercaseLetterRule,
                          },
                          {
                            validator: digitRule,
                          },
                          {
                            validator: specialCharRule,
                          },
                        ],
                      }}
                    >
                      <Input
                        config={{
                          type: { name: 'password' },
                          height: 'xlarge',
                          width: 'full',
                        }}
                      ></Input>
                    </Form.Item>
                    <Space config={{ way: 'vertical', count: 2 }}></Space>
                    <Form.Item
                      config={{
                        name: 'confirmNewPassword',
                        label: t('confirmNewPassword'),
                        way: 'vertical',
                        rules: [
                          { required: true },
                          {
                            validator: minLengthPasswordRule,
                          },
                          {
                            validator: uppercaseLetterRule,
                          },
                          {
                            validator: lowercaseLetterRule,
                          },
                          {
                            validator: digitRule,
                          },
                          {
                            validator: specialCharRule,
                          },
                        ],
                      }}
                    >
                      <Input
                        config={{
                          type: { name: 'password' },
                          height: 'xlarge',
                          width: 'full',
                        }}
                      ></Input>
                    </Form.Item>
                    <Space config={{ way: 'vertical', count: 2 }}></Space>
                    <Button
                      config={{
                        text: t('registerPassword'),
                        type: { value: 'submit' },
                        disabled: !(passwordValidFormat && passwordValidSame),
                      }}
                    ></Button>
                    <Space config={{ way: 'vertical', count: 2 }} />

                    {passwordValidFormat && !passwordValidSame && (
                      <Col
                        config={{
                          width: 'full',
                          vertical: 'center',
                          horizontal: 'center',
                        }}
                      >
                        <Write
                          data={{ item: t('passwordsAreDifferent') }}
                          config={{ color: 'error', mode: 'key-small-light' }}
                        />
                      </Col>
                    )}
                  </Form>
                </Widget>
              </div>
            </div>
          </Zone.Area>
        </Zone>
      )}
    </div>
  );
});
